import { ColDef, ColumnState, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef, useState } from "react";

import {
	aggridHeaderClass,
	commonBorderStyle,
	commonNumberCellStyle,
	commonTextCellStyle,
} from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultWmsTransportDetailDto } from "src/dtos/wmsTransport/wmsTransportDto";

import { SAL2100_R01MonthlyDto } from "../Dto_SAL2100_R01";

type SalesGridRow = SAL2100_R01MonthlyDto & { inserted?: boolean };

const formatNumber = (value: number | null | undefined) =>
	Number(value ?? 0).toLocaleString("ko-KR", { maximumFractionDigits: 2 });

export const SalesTable_SAL2100_R01 = ({ data }: { data: SAL2100_R01MonthlyDto[] }) => {
	const gridRef = useRef<AgGridReact<SalesGridRow>>(null);
	const [, setHoveredRowIndex] = useState<number | null>(null);
	const [, setColumnState] = useState<ColumnState[]>([]);
	const [scrollTop, setScrollTop] = useState<number | null>(null);
	const rowData = useMemo<SalesGridRow[]>(() => data.map((item) => ({ ...item })), [data]);
	const totalSales = data.reduce((sum, item) => sum + item.salesAmount, 0);
	const totalCost = data.reduce((sum, item) => sum + item.costAmount, 0);
	const totalProfitRate = totalSales === 0 ? 0 : ((totalSales - totalCost) / totalSales) * 100;
	const columnDefs = useMemo<ColDef<SalesGridRow>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "월",
			field: "mm",
			colId: "mm",
			flex: 1,
			minWidth: 70,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonTextCellStyle, textAlign: "center" }),
			valueFormatter: (params: ValueFormatterParams<SalesGridRow>) =>
				params.node?.rowPinned ? "합계" : `${params.value}월`,
		},
		{
			headerName: "매출액",
			field: "salesAmount",
			colId: "salesAmount",
			flex: 1.4,
			minWidth: 110,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonNumberCellStyle }),
			valueFormatter: (params) => formatNumber(params.value),
		},
		{
			headerName: "매출원가",
			field: "costAmount",
			colId: "costAmount",
			flex: 1.4,
			minWidth: 110,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonNumberCellStyle }),
			valueFormatter: (params) => formatNumber(params.value),
		},
		{
			headerName: "이익률",
			field: "profitRate",
			colId: "profitRate",
			flex: 1,
			minWidth: 85,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonNumberCellStyle }),
			valueFormatter: (params) => `${formatNumber(params.value)}%`,
		},
	], []);
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(columnDefs, []),
		[columnDefs],
	);
	const totalRow = useMemo<SalesGridRow[]>(() => [{
		...defaultWmsTransportDetailDto,
		mm: 0,
		salesAmount: totalSales,
		costAmount: totalCost,
		profitRate: totalProfitRate,
	}], [totalCost, totalProfitRate, totalSales]);

	return (
		<GridContextWrapper<SalesGridRow>
			gridRef={gridRef}
			paddedGridData={rowData}
			initSelected={() => {}}
			defaultRowData={{
				...defaultWmsTransportDetailDto,
				mm: 0,
				salesAmount: 0,
				costAmount: 0,
				profitRate: 0,
			}}
			baseColumnDefs={columnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={(state) => setColumnState(state ?? [])}
			localStorageKey="SAL2100_R01-sales-grid-column-state"
			menuItems={undefined}
			enableDataHistory={false}
		>
			<Grid_Touch<SalesGridRow>
				ref={gridRef}
				rowData={rowData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				domLayout="autoHeight"
				getRowId={(params) => String(params.data.mm)}
				onCellKeyDown={undefined}
				pinnedBottomRowData={totalRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey="SAL2100_R01-sales-grid-column-state"
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				enableCellTextSelection
			/>
		</GridContextWrapper>
	);
};
